import { createFileRoute } from "@tanstack/react-router";
import type { RepairResult, RepairScope } from "api-types/optionCatalogs/optionCatalogRepairRequests";
import { repairScopeValues } from "api-types/optionCatalogs/optionCatalogRepairRequests";
import { WEB_API_BASE_PATH } from "common/constants";
import Toggle from "lib/components/actions/Toggle";
import { postWithTimeout } from "lib/utils/fetchAuthHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import { useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/Checkbox";
import Input from "ui/forms/Input";
import withAuthentication from "../../components/withAuthentication";
import styles from "./option-catalog-repair.module.scss";

const SCOPE_LABELS: Record<RepairScope, string> = {
  divergentValues: "Fix divergent values",
  repeatHistory: "Clean repeat versions",
  flipFlopHistory: "Clean flip-flop versions",
};

function OptionCatalogRepairPage() {
  const [builderId, setBuilderId] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [selectedScopes, setSelectedScopes] = useState<Set<RepairScope>>(new Set(repairScopeValues));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RepairResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buttonLabel = dryRun ? "Scan" : "Repair";

  const toggleScope = (scope: RepairScope, enabled: boolean) => {
    setSelectedScopes((prev) => {
      const next = new Set(prev);
      if (enabled) {
        next.add(scope);
      } else {
        next.delete(scope);
      }
      return next;
    });
  };

  const runRepair = async () => {
    if (!builderId || selectedScopes.size === 0) {
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const resp = await postWithTimeout(
        `${WEB_API_BASE_PATH}/option-catalog-repair/run`,
        { builderId, dryRun, scopes: Array.from(selectedScopes) },
        120_000,
      );
      if (!resp.ok) {
        const body = await resp.json();
        throw new Error(body.error ?? `Request failed with status ${resp.status}`);
      }
      const data: RepairResult = await resp.json();
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Head>
        <title>{getPageTitle("Option Catalog Repair")}</title>
      </Head>
      <div className={styles.wrapper}>
        <h1>Option Catalog Repair</h1>

        <div className={styles.controls}>
          <div className={styles.field}>
            <label htmlFor="builderId">Builder ID (encoded)</label>
            <Input
              id="builderId"
              value={builderId}
              onChange={(e) => setBuilderId(e.target.value)}
              placeholder="Encoded builder ID"
              style={{ width: "200px" }}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="dryRun">Dry run</label>
            <Toggle id="dryRun" isTrue={dryRun} onChange={(val) => setDryRun(val)} />
          </div>
        </div>

        <div className={styles.field}>
          <label>Repair scopes</label>
          <div className={styles.scopeList}>
            {repairScopeValues.map((scope) => (
              <Checkbox
                key={scope}
                label={SCOPE_LABELS[scope]}
                checked={selectedScopes.has(scope)}
                onChangeBoolean={(val) => toggleScope(scope, val)}
              />
            ))}
          </div>
        </div>

        <div>
          <Button btnSize="medium" onClick={runRepair} disabled={loading || !builderId || selectedScopes.size === 0}>
            {loading ? "Running..." : buttonLabel}
          </Button>
        </div>

        {loading && <div className={styles.spinner}>Scanning option sets... this may take a minute.</div>}

        {error && <div className={styles.error}>{error}</div>}

        {result &&
          (() => {
            const totalVersionsToDelete = result.invalidVersionEntries.reduce((sum, o) => sum + o.deleteCount, 0);
            return (
              <div className={styles.results}>
                {result.scopes.includes("divergentValues") && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <h3>Divergent Option Values</h3>
                      <span
                        className={result.divergentCatalogValues.length > 0 ? styles.badgeWarning : styles.badgeSuccess}
                      >
                        {result.divergentCatalogValues.length} found
                      </span>
                      {result.applied && result.divergentCatalogValues.length > 0 && (
                        <span className={styles.badgeSuccess}>Fixed</span>
                      )}
                    </div>
                    {result.divergentCatalogValues.length > 0 ? (
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Option Set</th>
                            <th>Current Name</th>
                            <th>Source Name</th>
                            <th>Fields</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.divergentCatalogValues.map((opt) => (
                            <tr key={opt.builderOptionId}>
                              <td>{opt.builderOptionSetName}</td>
                              <td>{opt.currentName}</td>
                              <td>{opt.sourceName}</td>
                              <td>{opt.fields.join(", ")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>No divergent options. Current values match source docs.</p>
                    )}
                  </div>
                )}

                {(result.scopes.includes("repeatHistory") || result.scopes.includes("flipFlopHistory")) && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <h3>Version History Cleanup</h3>
                      <span className={totalVersionsToDelete > 0 ? styles.badgeWarning : styles.badgeSuccess}>
                        {totalVersionsToDelete} versions to remove across {result.invalidVersionEntries.length} options
                      </span>
                      {result.applied && totalVersionsToDelete > 0 && (
                        <span className={styles.badgeSuccess}>Deleted</span>
                      )}
                    </div>
                    {result.invalidVersionEntries.length > 0 ? (
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Option Name</th>
                            <th>Versions to Delete</th>
                            <th>Total Versions</th>
                            <th>Option ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.invalidVersionEntries.map((opt) => (
                            <tr key={opt.builderOptionId}>
                              <td>{opt.optionName}</td>
                              <td>{opt.deleteCount}</td>
                              <td>{opt.totalVersions}</td>
                              <td className={styles.mono}>{opt.builderOptionId}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p>No version history issues detected.</p>
                    )}
                  </div>
                )}

                {result.warnings.length > 0 && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <h3>Warnings</h3>
                      <span className={styles.badgeWarning}>{result.warnings.length}</span>
                    </div>
                    <ul>
                      {result.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {!result.applied && totalVersionsToDelete + result.divergentCatalogValues.length > 0 && (
                  <p>
                    This was a dry run. Toggle off &quot;Dry run&quot; and click <strong>Repair</strong> to apply
                    changes.
                  </p>
                )}
              </div>
            );
          })()}
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/option-catalog-repair")({
  component: withAuthentication(OptionCatalogRepairPage, { superAdmin: true }),
});
